import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Circle, TrendingUp } from 'lucide-react';
import api from '../services/api';
import { ComplianceTierMedaille, isMedailleTier, type ComplianceTier, type TierIndicatoren } from './ComplianceTierMedaille';

interface TierStatus {
  tier: ComplianceTier;
  eisen: TierIndicatoren;
  /** Discipline staat op de Zilver-lijst (arbo-specialist) */
  arbo_rol: boolean;
  /** Continuous Vetting (migratie 166): actieve NON_COMPLIANT_HOLD */
  hold: {
    ernst: 'LICHT' | 'ZWAAR';
    redenen: { code: 'KVK_NIET_ACTIEF' | 'POLIS_VERLOPEN' | 'REGISTER_VERLOPEN' | 'BIG_NIET_ACTIEF' | 'TUCHT_ONTBREEKT'; detail: string; sinds: string | null }[];
    gestart_op: string;
    herstel_deadline: string;
  } | null;
}

const BASIS_EISEN: { sleutel: 'kvk' | 'polis'; naar: string }[] = [
  { sleutel: 'kvk', naar: '/professional/belagom?view=uitbetalingen' },
  { sleutel: 'polis', naar: '/professional/belagom?view=screening' },
];

/**
 * Compliance Vetting Tier in de cockpit van de professional (migratie 175):
 * medaille + uitgerold lint met de stimulans naar de volgende tier,
 * compliance-hold → rode melding met redenen en hersteltermijn,
 * zonder KvK of polis → checklist van de twee basiseisen.
 */
export const ComplianceKeurmerkKaart: React.FC = () => {
  const { t } = useTranslation('professional');
  const { data } = useQuery<TierStatus>({
    queryKey: ['compliance-tier'],
    queryFn: async () => (await api.get('/professional/kwaliteitspaspoort/keurmerk')).data.data,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  if (!data) return null;

  if (data.tier === 'NON_COMPLIANT_HOLD' && data.hold) {
    const deadline = new Date(data.hold.herstel_deadline);
    const verstreken = data.hold.ernst === 'ZWAAR' || deadline.getTime() <= Date.now();
    return (
      <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4" role="alert">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-700">
            <AlertTriangle size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-red-900">{t('tier.hold.title')}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-red-800">
              {data.hold.redenen.map((r, i) => (
                <li key={`${r.code}-${i}`}>
                  {t(`tier.hold.codes.${r.code}`)}
                  {r.detail && <span className="text-red-700/80"> — {r.detail}</span>}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-red-800">
              {verstreken
                ? t('tier.hold.invisible')
                : t('tier.hold.deadline', {
                    date: deadline.toLocaleString('nl-NL', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }),
                  })}
            </p>
            <Link
              to="/professional/belagom?view=screening"
              className="mt-3 inline-flex items-center rounded-lg bg-red-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-800"
            >
              {t('tier.hold.upload')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isMedailleTier(data.tier)) {
    return (
      <div className="mb-4 flex flex-col items-center gap-4 rounded-xl bg-gradient-to-r from-[#0b1f4d] to-[#13306e] p-4 text-white sm:flex-row sm:items-start">
        <ComplianceTierMedaille tier={data.tier} indicatoren={data.eisen} size={96} uitgerold />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{t(`tier.titel.${data.tier}`)}</p>
          <p className="mt-1 text-sm text-blue-100">{t(`tier.uitleg.${data.tier}`)}</p>
          {data.tier !== 'GOUD' && (
            <div className="mt-3 flex items-start gap-2 rounded-lg bg-white/10 p-3 text-sm">
              <TrendingUp size={16} className="mt-0.5 flex-shrink-0 text-[#e8c75a]" />
              <span>
                {t('tier.naarGoud')}{' '}
                <Link to="/professional/belagom?view=screening" className="font-semibold text-[#e8c75a] hover:underline">
                  {t('tier.regelen')}
                </Link>
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
          <TrendingUp size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-amber-900">{t('tier.nudge')}</p>
          <p className="mt-1 text-sm text-amber-800">{t('tier.nudgeBody')}</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {BASIS_EISEN.map(({ sleutel, naar }) => {
              const voldaan = data.eisen[sleutel];
              return (
                <li key={sleutel} className="flex items-start gap-2 text-sm">
                  {voldaan ? (
                    <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0 text-green-600" />
                  ) : (
                    <Circle size={16} className="mt-0.5 flex-shrink-0 text-amber-500" />
                  )}
                  <span className={voldaan ? 'text-gray-600' : 'text-gray-900'}>
                    {t(`tier.eisen.${sleutel}`)}
                    {!voldaan && (
                      <Link to={naar} className="ml-1 font-medium text-[#003087] hover:underline">
                        {t('tier.regelen')}
                      </Link>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ComplianceKeurmerkKaart;
